import i18next from 'i18next';

import { TimeOffAllocationFormat } from '@/modules/common/types/time-off-type';
import { formatDisplayDate } from '@/utils/date-formatters';

import { TimeOffBalanceInfo } from '../types/time-off-balance';

const TIME_OFF_NS = 'authenticated.timeOff';

/**
 * Builds the ordered bullet items shown in a balance's info tooltip from its
 * policy-derived {@link TimeOffBalanceInfo}. Conditional bullets (carryover) are
 * omitted when not applicable; the rest always render. Centralised so the
 * expanded card and the collapsed compact strip stay in sync.
 */
export const buildBalanceInfoTooltipItems = (info: TimeOffBalanceInfo): string[] => {
  const t = (key: string, options?: Record<string, unknown>) =>
    i18next.t(`${TIME_OFF_NS}:${key}`, options ?? {});

  const items: string[] = [];

  // 1. Allocation — monthly vs yearly phrasing.
  if (info.allocationFormat === TimeOffAllocationFormat.Monthly) {
    items.push(
      t('balances.infoTooltip.allocationMonthly', {
        count: info.numberOfDays,
        day: info.renewalDay,
      })
    );
  } else {
    // Yearly: "<Month> <day>" rendered from a synthetic date (month is 1-based).
    const renewalDate = new Date(2000, info.renewalMonth - 1, info.renewalDay);
    items.push(
      t('balances.infoTooltip.allocationYearly', {
        count: info.numberOfDays,
        date: formatDisplayDate(renewalDate),
      })
    );
  }

  // 2. Carryover — only when the policy allows it.
  if (info.allowCarryover && info.carryoverDays != null) {
    items.push(t('balances.infoTooltip.carryover', { count: info.carryoverDays }));
  }

  // 3. Minimum possible balance — negative limit when allowed, else 0.
  if (info.allowNegativeBalance) {
    items.push(
      t('balances.infoTooltip.minBalanceNegative', { count: info.negativeBalanceLimit ?? 0 })
    );
  } else {
    items.push(t('balances.infoTooltip.minBalanceZero'));
  }

  // 4. Maximum possible balance.
  if (info.maximumBalanceLimit != null) {
    items.push(t('balances.infoTooltip.maxBalance', { count: info.maximumBalanceLimit }));
  }

  // 5. Payable / not payable.
  items.push(
    t(info.isBillable ? 'balances.infoTooltip.payable' : 'balances.infoTooltip.notPayable')
  );

  return items;
};
